import React, { useEffect, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import * as XLSX from "xlsx";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import RefreshIcon from '@mui/icons-material/Refresh';


const theme = createTheme({
  palette: {
    primary: {
      main: "#dc2626",
    },
    secondary: {
      main: "#eab308",
    },
  },
});


const TodayInvoicesGrid = ({ invoices = [] }) => {
  const [items, setItems] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState({});
  // State to track scroll position
  const [scrollPosition, setScrollPosition] = useState(0);

  // **ADD THESE HELPER FUNCTIONS AFTER THE IMPORTS AND BEFORE THE COMPONENT**

// UK Time helper functions
const getUKTime = (date = new Date()) => {
  return new Date(date.toLocaleString("en-US", { timeZone: "Europe/London" }));
};

const formatUKTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  let date;
  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else {
    date = new Date(timestamp);
  }
  
  return getUKTime(date).toLocaleString("en-GB", { 
    timeZone: "Europe/London",
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getTodayRangeText = () => {
  const ukNow = getUKTime();
  const todayStart = new Date(ukNow);
  todayStart.setHours(9, 0, 0, 0);
  
  if (ukNow.getHours() < 9) {
    todayStart.setDate(todayStart.getDate() - 1);
  }
  
  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);
  todayEnd.setHours(9, 0, 0, 0);
  
  return {
    start: todayStart.toLocaleDateString("en-GB", { 
      timeZone: "Europe/London",
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit',
      minute: '2-digit'
    }),
    end: todayEnd.toLocaleDateString("en-GB", { 
      timeZone: "Europe/London",
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit',
      minute: '2-digit'
    })
  };
};

  useEffect(() => {
    const fetchCategories = async () => {
      const db = getFirestore();
      const categoriesSnapshot = await getDocs(collection(db, "inventoryCategory"));
      const categoriesData = {};

      categoriesSnapshot.forEach((doc) => {
        categoriesData[doc.id] = doc.data().category;
      });

      setCategories(categoriesData);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (invoices && invoices.length > 0 && Object.keys(categories).length > 0) {
      processInvoices();
    }
  }, [invoices, categories]);

  // Add effect to update category headers position when scrolling
  useEffect(() => {
    const categoryHeaders = document.querySelectorAll('.category-label');
    categoryHeaders.forEach(header => {
      if (header) {
        header.style.transform = `translateX(${scrollPosition}px)`;
      }
    });
  }, [scrollPosition]);

  // **ADD THIS REFRESH FUNCTION**
  const handleRefresh = async () => {
    console.log("🔄 Refreshing Today's Invoices Grid...");
    setLoading(true);

    try {
      // **Re-fetch categories** 
      const db = getFirestore();
      const categoriesSnapshot = await getDocs(collection(db, "inventoryCategory"));
      const categoriesData = {};

      categoriesSnapshot.forEach((doc) => {
        categoriesData[doc.id] = doc.data().category;
      });

      console.log("🏷️ Categories refreshed:", Object.keys(categoriesData).length);
      setCategories(categoriesData);

      // **Re-process invoices with fresh data**
      if (invoices && invoices.length > 0) {
        await processInvoices();
        console.log("✅ Invoices grid refreshed successfully");
      } else {
        console.log("⚠️ No invoices to refresh");
      }

    } catch (error) {
      console.error("❌ Error refreshing invoices grid:", error);
    } finally {
      setLoading(false);
    }
  };


  // Handle scroll event
  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
  };

  const processInvoices = async () => {
    try {
      setLoading(true);

      // Collect all unique items across invoices
      const uniqueItems = new Set();
      invoices.forEach((invoice) =>
        invoice.items.forEach((item) => uniqueItems.add(item.title))
      );

      const itemList = Array.from(uniqueItems).map((title) => ({ title }));

      // Get unique restaurant names
      const restaurantNames = [...new Set(invoices.map(invoice => invoice.restaurantName))];

      // Generate restaurant-wise item quantities
      const restaurantGrid = restaurantNames.map((restaurantName) => {
        const orders = invoices.filter(
          (invoice) => invoice.restaurantName === restaurantName
        );

        const itemQuantities = itemList.map((item) => {
          const quantity = orders
            .flatMap((order) => order.items)
            .filter((orderItem) => orderItem.title === item.title)
            .reduce((sum, orderItem) => sum + (orderItem.quantity || 0), 0);
          return { title: item.title, quantity };
        });

        return { restaurantName, itemQuantities };
      });

      // Categorize items using categoryId from items
      const categorizedItems = {};

      invoices.forEach((invoice) => {
        invoice.items.forEach((item) => {
          // Get category name from categories map using categoryId
          const categoryName = categories[item.categoryId] || "Uncategorized";

          if (!categorizedItems[categoryName]) {
            categorizedItems[categoryName] = [];
          }

          if (!categorizedItems[categoryName].some((i) => i.title === item.title)) {
            categorizedItems[categoryName].push(item);
          }
        });
      });

      setItems(categorizedItems);
      setData(restaurantGrid);
    } catch (error) {
      console.error("Error processing invoices: ", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    // Header row
    const headers = ["Item", ...data.map((d) => d.restaurantName), "Total"];
    worksheetData.push(headers);

    // Iterate over categories and items
    Object.entries(items).forEach(([category, categoryItems]) => {
      // Add category row (spanning columns)
      worksheetData.push([category, ...Array(data.length + 1).fill("")]);

      // Add items within category
      categoryItems.forEach((item) => {
        const row = [item.title];
        let rowTotal = 0;

        data.forEach((restaurant) => {
          const quantity =
            restaurant.itemQuantities.find((q) => q.title === item.title)
              ?.quantity || 0;
          row.push(quantity);
          rowTotal += quantity;
        });

        row.push(rowTotal);
        worksheetData.push(row);
      });
    });

    // Grand Total Row
    const totalRow = ["Total"];
    let grandTotal = 0;

    data.forEach((restaurant) => {
      const restaurantTotal = restaurant.itemQuantities.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      totalRow.push(restaurantTotal);
      grandTotal += restaurantTotal;
    });

    totalRow.push(grandTotal);
    worksheetData.push(totalRow);

    // Convert to worksheet and save
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
    XLSX.writeFile(workbook, "todays_invoices.xlsx");
  };

  const downloadPDF = () => {
  try {
    // Check if jsPDF is available
    if (typeof jsPDF === 'undefined') {
      console.error('jsPDF is not available');
      alert('PDF generation is not available. Please try again.');
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3",
    });

    // Check if autoTable method exists
    if (typeof doc.autoTable !== 'function') {
      console.error('autoTable method is not available');
      alert('PDF table generation is not available. Please try again.');
      return;
    }

    const headers = ["Item", ...data.map((d) => d.restaurantName), "Total"];
    const rows = [];

    // Process data with category markers
    Object.entries(items).forEach(([category, categoryItems]) => {
      // Mark category rows
      rows.push({
        content: [category, ...Array(data.length + 1).fill("")],
        isCategory: true
      });

      // Add items
      categoryItems.forEach((item) => {
        const row = [item.title];
        let rowTotal = 0;

        data.forEach((restaurant) => {
          const quantity = restaurant.itemQuantities.find((q) => q.title === item.title)?.quantity || 0;
          row.push(quantity.toString());
          rowTotal += quantity;
        });

        row.push(rowTotal.toString());
        rows.push({
          content: row,
          isCategory: false
        });
      });
    });

    // Simple title
    doc.setFontSize(26);
    doc.text("Today's Orders Grid", 170, 15);

    // Simple table with darker and thicker grid lines
    doc.autoTable({
      head: [headers],
      body: rows.map(row => row.content),
      startY: 20,
      theme: 'grid',
      styles: {
        fontSize: 13,
        cellPadding: 3,
        font: 'helvetica',
        lineColor: [0, 0, 0], // Pure black grid lines
        lineWidth: 0.3, // Thicker grid lines
      },
      headStyles: {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 12,
        font: 'helvetica',
        lineColor: [0, 0, 0], // Black borders for header
        lineWidth: 0.4, // Even thicker borders for header
      },
      bodyStyles: {
        font: 'helvetica',
        lineColor: [0, 0, 0], // Black borders for body
        lineWidth: 0.3, // Thicker borders for body
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245], // Light gray for alternate rows
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
      },
      // Safe way to style category rows
      willDrawCell: (data) => {
        // Check if data exists and has row property
        if (data && data.row && typeof data.row.index !== 'undefined') {
          const rowIndex = data.row.index;
          const isCategory = rows[rowIndex] && rows[rowIndex].isCategory;
          
          if (isCategory) {
            // Style category rows with darker background and thicker borders
            doc.setFillColor(180, 180, 180); // Darker gray
            doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
            
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            
            doc.text(
              data.cell.raw,
              data.cell.x + 4,
              data.cell.y + data.cell.height / 2 + 2
            );
            
            // Draw thicker border around category cells
            doc.setDrawColor(0, 0, 0); // Black border
            doc.setLineWidth(0.5); // Thicker border for categories
            doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height);
          }
        }
      }
    });

    doc.save("todays_orders.pdf");
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF: ' + error.message);
  }
};


  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={300}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Box p={2}>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            "& .MuiAlert-icon": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <AlertTitle>No Orders Found</AlertTitle>
          There are no orders for today. New orders will appear here as they
          come in.
        </Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          marginLeft: "3.5%",
          marginTop: "15px",
          maxWidth: "95%",
        }}
      >
        {/* **UPDATED TITLE SECTION WITH REFRESH BUTTON** */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            variant="h4"
            color="text.primary"
            sx={{
              fontWeight: "bold",
              flex: 1,
              minWidth: '300px'
            }}
          >
            Today's Orders Grid By Restaurant
          </Typography>
          <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontStyle: 'italic' }}
    >
      📍 UK Time Range: {getTodayRangeText().start} to {getTodayRangeText().end}
    </Typography>

          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>


        {/* Table Container */}
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            position: "relative",
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
            },
          }}
          onScroll={handleScroll}
        >
          <table
            id="invoice-table"
            style={{
              minWidth: "100%",
              width: "max-content",
              borderCollapse: "collapse",
              borderSpacing: 0,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    backgroundColor: "#e8f5e9",
                    color: "#1976d2",
                    padding: "16px",
                    border: "1px solid #ffffff",
                    minWidth: "150px",
                    maxWidth: "250px",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                >
                  Item
                </th>
                {data.map((restaurant, index) => (
                  <th
                    key={index}
                    style={{
                      backgroundColor: "#e8f5e9",
                      color: "#1976d2",
                      padding: "16px",
                      border: "1px solid #ffffff",
                      minWidth: "150px",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }}
                  >
                    {restaurant.restaurantName}
                  </th>
                ))}
                <th
                  style={{
                    backgroundColor: "#e8f5e9",
                    color: "#1976d2",
                    padding: "16px",
                    border: "1px solid #ffffff",
                    minWidth: "70px",
                    position: "sticky",
                    right: 0,
                    zIndex: 3,
                  }}
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(items).map(([category, categoryItems], catIndex) => (
                <React.Fragment key={catIndex}>
                  {/* Category Divider with fixed position */}
                  <tr>
                    <td
                      colSpan={data.length + 2}
                      style={{
                        position: "relative",
                        padding: 0,
                        height: "40px",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {/* Fixed Category Label - this will stay in place during scroll */}
                      <div
                        className="category-label"
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          zIndex: 4,
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          textAlign: "left",
                          padding: "10px",
                          border: "1px solid #ddd",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {category}
                      </div>
                    </td>
                  </tr>

                  {/* Render Items in Category */}
                  {categoryItems.map((item, index) => {
                    const total = data.reduce((sum, restaurant) => {
                      const quantity =
                        restaurant.itemQuantities.find(
                          (q) => q.title === item.title
                        )?.quantity || 0;
                      return sum + quantity;
                    }, 0);

                    return (
                      <tr key={index}>
                        {/* Sticky Item Column */}
                        <td
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            fontWeight: "500",
                            position: "sticky",
                            left: 0,
                            backgroundColor: "#fff",
                            zIndex: 2,
                          }}
                        >
                          {item.title}
                        </td>

                        {/* Restaurant Data */}
                        {data.map((restaurant, i) => (
                          <td
                            key={i}
                            style={{
                              padding: "16px",
                              border: "1px solid #ddd",
                              textAlign: "center",
                            }}
                          >
                            {restaurant.itemQuantities.find(
                              (q) => q.title === item.title
                            )?.quantity || "-"}
                          </td>
                        ))}

                        {/* Sticky Total Column */}
                        <td
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            textAlign: "center",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 2,
                          }}
                        >
                          {total}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* Grand Total Row */}
              <tr style={{ backgroundColor: "#f1f1f1", fontWeight: "bold" }}>
                <td
                  style={{
                    padding: "16px",
                    border: "1px solid #ddd",
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f1f1f1",
                    zIndex: 2,
                  }}
                >
                  Total
                </td>

                {data.map((restaurant, i) => {
                  const grandTotal = restaurant.itemQuantities.reduce(
                    (sum, item) => sum + item.quantity,
                    0
                  );
                  return (
                    <td
                      key={i}
                      style={{
                        padding: "16px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                        backgroundColor: "#f1f1f1",
                      }}
                    >
                      {grandTotal}
                    </td>
                  );
                })}

                <td
                  style={{
                    padding: "16px",
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "center",
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#f1f1f1",
                    zIndex: 2,
                  }}
                >
                  {data.reduce(
                    (sum, restaurant) =>
                      sum +
                      restaurant.itemQuantities.reduce(
                        (s, i) => s + i.quantity,
                        0
                      ),
                    0
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadPDF}
            sx={{ minWidth: 150 }}
            startIcon={<PictureAsPdfIcon />}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={downloadExcel}
            sx={{ minWidth: 150 }}
            startIcon={<TableChartIcon />}
          >
            Download Excel
          </Button>
        </Box>
      </Paper>
    </ThemeProvider>
  );
};

export default TodayInvoicesGrid;